import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { OnOffSwitch } from "@/components/OnOffSwitch";

const move = (list, i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; };

function MoveBtns({ i, len, onMove, testid }) {
  const cls = "rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30";
  return (
    <span className="flex gap-0.5">
      <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} className={cls} aria-label="Move up" data-testid={`${testid}-up`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" disabled={i === len - 1} onClick={() => onMove(i, 1)} className={cls} aria-label="Move down" data-testid={`${testid}-down`}><ArrowDown className="h-4 w-4" /></button>
    </span>
  );
}

function EditorCard({ title, help, testid, onSave, children }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <h3 className="font-heading text-lg font-bold">{title}</h3>
      <p className="text-sm text-slate-500">{help}</p>
      {children}
      <Button type="button" onClick={onSave} data-testid={`${testid}-save`}>Save</Button>
    </section>
  );
}

const KINDS = [["pastor", "Pastor tithes"], ["church", "Church tithes"], ["registrations", "Conference Registration"], ["donations", "Conference Collection"], ["progress", "Progress Reports"], ["other", "Other Collection"]];

function AddChoice({ onAdd }) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState("pastor");
  const add = (e) => { e.preventDefault(); if (!label.trim()) return; onAdd({ key: `c_${Date.now().toString(36)}`, label: label.trim(), kind, visible: true, custom: true }); setLabel(""); };
  return (
    <form onSubmit={add} className="grid gap-2 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="only-add-form">
      <Input value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder="New choice name" className="h-10 bg-white text-slate-900" data-testid="only-add-label" />
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="only-add-kind">
        {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Button type="submit" variant="outline" className="h-10" data-testid="only-add-btn"><Plus className="mr-1 h-4 w-4" /> Add choice</Button>
    </form>
  );
}

export function ShowOnlyEditor({ settings, save }) {
  const [list, setList] = useState(settings.only_choices || []);
  const set = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const sec = settings.report_sec === "gc" ? "General Council" : "MWJK";
  return (
    <EditorCard title={`"Show only" choices — ${sec}`} help="Rename, hide or reorder the choices in Reports → Show only. Applies only to the side picked above." testid="only-choices-card" onSave={() => save({ only_choices: list })}>
      {list.map((o, i) => (
        <div key={o.key} className="flex items-center gap-2" data-testid={`only-choice-row-${o.key}`}>
          <MoveBtns i={i} len={list.length} onMove={(a, d) => setList((l) => move(l, a, d))} testid={`only-choice-${o.key}`} />
          <Input value={o.label} maxLength={80} onChange={(e) => set(i, { label: e.target.value })} className="h-10 flex-1 bg-white text-slate-900" data-testid={`only-choice-label-${o.key}`} />
          {o.custom && <span className="hidden text-xs text-slate-500 sm:inline" data-testid={`only-choice-kind-${o.key}`}>{KINDS.find(([k]) => k === o.kind)?.[1]}</span>}
          <OnOffSwitch checked={o.visible} onChange={(v) => set(i, { visible: v })} testid={`only-choice-visible-${o.key}`} />
          {o.custom && <button type="button" onClick={() => setList((l) => l.filter((x) => x.key !== o.key))} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Remove choice" data-testid={`only-choice-remove-${o.key}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <AddChoice onAdd={(c) => setList((l) => [...l, c])} />
    </EditorCard>
  );
}

function AddColumn({ onAdd }) {
  const [label, setLabel] = useState("");
  const [desc, setDesc] = useState("");
  const add = (e) => { e.preventDefault(); if (!label.trim()) return; onAdd({ key: `c_${Date.now().toString(36)}`, label: label.trim(), desc: desc.trim(), visible: true, custom: true }); setLabel(""); setDesc(""); };
  return (
    <form onSubmit={add} className="grid gap-2 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="progress-add-form">
      <Input value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder="New column name" className="h-10 bg-white text-slate-900" data-testid="progress-add-label" />
      <Input value={desc} maxLength={160} onChange={(e) => setDesc(e.target.value)} placeholder="Short description" className="h-10 bg-white text-slate-900" data-testid="progress-add-desc" />
      <Button type="submit" variant="outline" className="h-10" data-testid="progress-add-btn"><Plus className="mr-1 h-4 w-4" /> Add column</Button>
    </form>
  );
}

export function ProgressFieldsEditor({ settings, save }) {
  const [list, setList] = useState(settings.progress_fields || []);
  const set = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const sec = settings.report_sec === "gc" ? "General Council" : "MWJK";
  return (
    <EditorCard title={`Progress Report columns — ${sec}`} help="Rename, describe, hide, reorder or add your own number columns. Used in the Progress Report form, Reports tables and all exports. Remember to press Save." testid="progress-columns-card" onSave={() => save({ progress_fields: list })}>
      {list.map((f, i) => (
        <div key={f.key} className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3" data-testid={`progress-col-row-${f.key}`}>
          <MoveBtns i={i} len={list.length} onMove={(a, d) => setList((l) => move(l, a, d))} testid={`progress-col-${f.key}`} />
          <div className="grid min-w-0 flex-1 gap-1 sm:grid-cols-2">
            <Input value={f.label} maxLength={80} onChange={(e) => set(i, { label: e.target.value })} className="h-10 bg-white text-slate-900" data-testid={`progress-col-label-${f.key}`} />
            <Input value={f.desc} maxLength={160} onChange={(e) => set(i, { desc: e.target.value })} placeholder="Short description" className="h-10 bg-white text-slate-900" data-testid={`progress-col-desc-${f.key}`} />
          </div>
          <OnOffSwitch checked={f.visible} onChange={(v) => set(i, { visible: v })} testid={`progress-col-toggle-${f.key}`} />
          {f.custom && <button type="button" onClick={() => setList((l) => l.filter((x) => x.key !== f.key))} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Remove column" data-testid={`progress-col-remove-${f.key}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <AddColumn onAdd={(c) => setList((l) => [...l, c])} />
    </EditorCard>
  );
}
